import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useConfig } from '@config/provider';
import { useAuthSession } from '@app/context/auth-session';
import { buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { streamSse } from '@app/utils/sse';

const REFRESH_THRESHOLD_MS = 60 * 1000;

interface OrgChangeEvent {
  id?: string;
  orgId?: string;
  resource?: string;
  resourceId?: string;
  action?: 'create' | 'update' | 'delete';
  timestamp?: string;
  actorId?: string;
}

interface OrgChangefeedContextValue {
  lastEvent: OrgChangeEvent | null;
}

const OrgChangefeedContext = createContext<OrgChangefeedContextValue | null>(null);

export const OrgChangefeedProvider = ({
  orgId,
  children
}: {
  orgId: string | null;
  children: ReactNode;
}) => {
  const { tokens, refreshTokens } = useAuthSession();
  const { apiBaseUrl, mockRoutes } = useConfig();
  const basePath = useMemo(() => resolveBasePath(apiBaseUrl), [apiBaseUrl]);
  const actorId = useMemo(() => decodeJwtSubject(tokens?.accessToken), [tokens?.accessToken]);
  const [lastEvent, setLastEvent] = useState<OrgChangeEvent | null>(null);
  const mocksEnabled = useMemo(() => Array.isArray(mockRoutes) && mockRoutes.length > 0, [mockRoutes]);
  const tokensRef = useRef(tokens);
  const refreshTokensRef = useRef(refreshTokens);
  const actorIdRef = useRef(actorId);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  useEffect(() => {
    actorIdRef.current = actorId;
  }, [actorId]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    // When UI mocks are enabled, there's typically no backend SSE endpoint to connect to.
    // Avoid noisy logs + repeated reconnect attempts.
    if (mocksEnabled) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const url = new URL(buildOrgUrl(basePath, orgId, 'events'));
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    void (async () => {
      let attempt = 0;
      while (!cancelled && !controller.signal.aborted) {
        const currentTokens = tokensRef.current;
        const shouldRefresh =
          Boolean(currentTokens?.refreshToken) &&
          typeof currentTokens?.expiresAt === 'number' &&
          Date.now() >= (currentTokens.expiresAt ?? 0) - REFRESH_THRESHOLD_MS;

        let accessToken = currentTokens?.accessToken ?? '';
        if (shouldRefresh) {
          const refreshed = await refreshTokensRef.current();
          accessToken = refreshed?.accessToken ?? accessToken;
        }

        try {
          await streamSse(
            url.toString(),
            {
              headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
              onEvent: (type, payload) => {
                if (cancelled || type !== 'change') {
                  return;
                }
                let event: OrgChangeEvent | null = null;
                try {
                  event = JSON.parse(payload) as OrgChangeEvent;
                } catch {
                  return;
                }
                if (shouldSkipSelfEvent(actorIdRef.current, event?.actorId)) {
                  console.debug('[org-changefeed] skip self event', {
                    actorId: actorIdRef.current,
                    eventId: event?.id,
                    resource: event?.resource,
                    resourceId: event?.resourceId,
                    action: event?.action
                  });
                  return;
                }
                setLastEvent(event);
              },
              onOpen: () => {
                attempt = 0;
              }
            },
            controller.signal
          );
        } catch (error) {
          if (controller.signal.aborted || cancelled) {
            return;
          }
          console.error('[org-changefeed] failed to stream org changefeed', error);
        }

        if (controller.signal.aborted || cancelled) {
          return;
        }
        const baseDelay = Math.min(30_000, 1_000 * 2 ** attempt);
        const jitter = Math.floor(Math.random() * 500);
        attempt = Math.min(attempt + 1, 6);
        await sleep(baseDelay + jitter);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [basePath, mocksEnabled, orgId]);

  const value = useMemo(() => ({ lastEvent }), [lastEvent]);

  return <OrgChangefeedContext.Provider value={value}>{children}</OrgChangefeedContext.Provider>;
};

export const useOrgChangefeedEvent = (): OrgChangeEvent | null => {
  const ctx = useContext(OrgChangefeedContext);
  return ctx?.lastEvent ?? null;
};

export const useOrgChangefeedEffect = (
  resources: string[] | null,
  onChange: (event: OrgChangeEvent) => void,
  deps: unknown[] = []
) => {
  const lastEvent = useOrgChangefeedEvent();
  const onChangeRef = useRef(onChange);
  const lastHandledRef = useRef<string | null>(null);
  const resourceKey = useMemo(() => {
    if (!resources || resources.length === 0) {
      return '';
    }
    return resources
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join('|');
  }, [resources]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange, ...deps]);

  useEffect(() => {
    if (!lastEvent) {
      return;
    }
    const eventKey =
      lastEvent.id ??
      [
        lastEvent.resource ?? '',
        lastEvent.resourceId ?? '',
        lastEvent.action ?? '',
        lastEvent.timestamp ?? ''
      ].join('|');
    if (eventKey && lastHandledRef.current === eventKey) {
      return;
    }
    if (resourceKey) {
      const resource = (lastEvent.resource ?? '').trim().toLowerCase();
      if (!resource) {
        return;
      }
      const allowed = new Set(resourceKey.split('|'));
      if (!allowed.has(resource)) {
        return;
      }
    }
    lastHandledRef.current = eventKey || null;
    onChangeRef.current(lastEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent, resourceKey]);
};

const decodeJwtSubject = (token?: string | null): string => {
  if (!token) {
    return '';
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return '';
  }
  try {
    const payload = base64UrlDecode(parts[1]);
    const parsed = JSON.parse(payload) as { sub?: unknown };
    if (typeof parsed.sub === 'string') {
      return parsed.sub.trim();
    }
  } catch {
    return '';
  }
  return '';
};

const base64UrlDecode = (value: string): string => {
  if (typeof atob !== 'function') {
    return '';
  }
  let base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) {
    base64 += '='.repeat(4 - pad);
  }
  return atob(base64);
};

const shouldSkipSelfEvent = (actorId: string, eventActorId?: string | null): boolean => {
  const actor = actorId.trim();
  if (!actor) {
    return false;
  }
  const eventActor = (eventActorId ?? '').trim();
  if (!eventActor) {
    return false;
  }
  return actor.toLowerCase() === eventActor.toLowerCase();
};
